import type { WorkdayConfig } from '@ztechapps/common.contract'
import { Duration, toDate } from '@ztechapps/common.utils'

/**
 * Compute the end date for a task calendar event.
 * Mirrors workday-config-aware duration calculation.
 *
 * - If timeSpent exists: adds weeks/days/hours/minutes via Duration.as() to startDate
 * - If no timeSpent: returns end of today in the user's timezone
 *
 * Returns an ISO string.
 */
export const computeTaskEndDate = (
  startDate: Date | string,
  timeSpent: number,
  workdayConfig: WorkdayConfig,
  timeZone?: string
): string => {
  if (!timeSpent) {
    return toDate(undefined, timeZone).endOf('day').toISOString()
  }
  const { week = 0, day = 0, hour = 0, minute = 0 } = Duration.as(timeSpent, workdayConfig)
  return toDate(startDate, timeZone)
    .add(week, 'weeks')
    .add(day, 'days')
    .add(hour, 'hours')
    .add(minute, 'minutes')
    .toISOString()
}

/**
 * Build the display title for a task.
 * Format: "T-001 → Task Name" (code + arrow + name), or just name if no code.
 */
export const buildTaskTitle = (item: { code?: string; name?: string }): string => {
  const code = item?.code
  const name = item?.name
  return code ? `${code} \u2192 ${name}` : (name ?? '')
}
